import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import { useBlueconicContext } from '@contexts/BlueconicContext';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { Theme } from '@interfaces/global';
import { article_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { captureBlueconicClickEvent } from '@utils/blueconic/utils';
import { UtmParams, articlePathWithParams } from '@utils/queryStringHelper';

interface ArticleHeaderProps {
  className?: string;
  offerCode?: string;
  slug: string;
  target?: string;
  title: string;
  theme?: Theme;
  utmParams?: UtmParams;
  newsletterSlug?: string;
}

const ArticleHeader = ({
  className,
  offerCode,
  slug,
  target,
  title,
  theme = Theme.Light,
  utmParams,
  newsletterSlug,
  ...rest
}: ArticleHeaderProps) => {
  const { trackEvent } = useEventTracking();
  const { variantId } = useBlueconicContext();
  const [articlePath, setArticlePath] = useState(article_path(slug));
  const hydrated = useServerSideRendering();

  const clickTargetCapture = () => {
    trackEvent(click({ clickTarget: ClickTarget.Headline, clickText: title }));
    if (variantId) {
      captureBlueconicClickEvent(variantId);
    }
  };

  useEffect(() => {
    if (!hydrated) return;
    setArticlePath(
      articlePathWithParams({
        slug: slug,
        offer: offerCode,
        utm: utmParams,
        newsletterSlug: newsletterSlug,
      })
    );
  }, [slug, offerCode, utmParams, hydrated, newsletterSlug]);

  return (
    <h3
      className={clsx('m-0 mb-4 font-suisse-works', className, {
        'text-black': theme === Theme.Light,
        'text-white': theme === Theme.Dark,
      })}
      {...rest}
    >
      <a
        href={articlePath}
        onClickCapture={clickTargetCapture}
        className={clsx('text-balance transition-colors duration-200', {
          'text-black': theme === Theme.Light,
          'text-white': theme === Theme.Dark,
        })}
        target={target}
      >
        <div className="hover:text-primary-magenta">{title}</div>
      </a>
    </h3>
  );
};

export default ArticleHeader;
